import { z } from "zod";
import { logger } from "../infra/logger";
import { numberId } from "./zodTypes";

export const RoomType = z.enum([
  "Office",
  "TeamRoom",
  "MeetingRoom",
  "Auditorium",
  "CommandCenter",
]);
export type RoomType = z.infer<typeof RoomType>;

export const AccessMode = z.enum(["Open", "KnockRequired", "DoNotDisturb"]);
export type AccessMode = z.infer<typeof AccessMode>;

export const AccessModeDetails = z.object({
  temporary: z.boolean(),
  dndReason: z.enum(["Zoom"]).optional(),
});
export type AccessModeDetails = z.infer<typeof AccessModeDetails>;

export const StageMode = z.enum(["Open", "AskToTalk", "Closed", "Backstage"]);
export type StageMode = z.infer<typeof StageMode>;

export const QAMode = z.boolean();
export type QAMode = z.infer<typeof QAMode>;

export const WillReturn = z.object({
  returnTime: z.number().positive(),
  absenceReason: z.string().optional(),
});
export type WillReturn = z.infer<typeof WillReturn>;

export const SeatAssignment = z.object({
  seatNumber: z.number().nonnegative(),
  personId: numberId(),
  name: z.string().nonempty().optional(),
  willReturn: WillReturn.optional(),
});
export type SeatAssignment = z.infer<typeof SeatAssignment>;

/** part of some rooms. may also contain a backstage **/
export const Stage = z.object({
  roomId: numberId(),
  stageMode: StageMode, // the modes by which Stage can be accessed by audience
  QAMode: QAMode, // true if audience may ask verbal or written questions
});
export type Stage = z.infer<typeof Stage>;

// NOTE(vple): It seems like this model is starting to have a lot going on. At some point, it seems
// like we might want to break it up a bit. At time of this comment, it seems like a few breaking
// points would be:
//  - "primary/core": room data (id, name, type, etc.)
//  - "map rendering": width, height, x, y, assigned seats, etc. (not exactly sure about assigned
//    seats...)
//  - aux/misc/in room?: saved boards
export const Room = z.object({
  id: numberId(),
  name: z.string().optional(),
  floorId: numberId(),
  roomType: RoomType,
  width: z.number().positive(),
  height: z.number().positive(),
  xLocation: z.number().positive(),
  yLocation: z.number().positive(),
  accessMode: AccessMode,
  accessModeDetails: AccessModeDetails.optional(),
  disableCanarySFU: z.boolean(),
  // Derived data
  assignedSeats: z.array(SeatAssignment).optional(),
  savedBoards: z.array(z.string()).optional(),
  stage: Stage.optional(),
});
export type Room = z.infer<typeof Room>;

/**
 * Request object for creating a new room.
 */
export const NewRoom = Room.pick({
  // Please keep fields in sync with cleanNewRoom, below.

  name: true,
  roomType: true,
  // allow room mode?
  // allow width/height?
  // allow x/y location?
  disableCanarySFU: true,
  assignedSeats: true,
});
export type NewRoom = z.infer<typeof NewRoom>;

/**
 * Cleans a NewRoom, returning only properties defined on the type.
 */
export const cleanNewRoom = (newRoom: NewRoom): NewRoom => {
  return {
    name: newRoom.name,
    roomType: newRoom.roomType,
    disableCanarySFU: newRoom.disableCanarySFU,
    assignedSeats: newRoom.assignedSeats,
  };
};

export const getRoomSeats = (room: Room): RoomSeat[] => {
  if (!room.assignedSeats) {
    return [];
  }

  return room.assignedSeats.map((assignedSeat) => ({
    roomId: room.id,
    seatAssignment: assignedSeat,
  }));
};

export const RoomSeat = z.object({
  roomId: numberId(),
  seatAssignment: SeatAssignment,
});
export type RoomSeat = z.infer<typeof RoomSeat>;

export const maxStageParticipants = 10; // indices 0-9 on stage
export const maxBackstageParticipants = 10; // indices 10-19 on backstage
export const maxFloorMicParticipants = 100; // indices 20-119 with floormic
export const maxInvisibleObservers = 5; // indices 120-124
export const firstBackstageIndex = maxStageParticipants;
export const lastBackstageIndex = firstBackstageIndex + maxBackstageParticipants - 1;
export const firstFloorMicIndex = lastBackstageIndex + 1;
export const lastFloorMicIndex = firstFloorMicIndex + maxFloorMicParticipants - 1;
export const firstInvisibleObserverIndex = lastFloorMicIndex + 1;
export const lastInvisibleObserverIndex = firstInvisibleObserverIndex + maxInvisibleObservers - 1;

/**
 * Find location subkind. If not in Auditorium then return RoomLocation
 * But if in Auditorium then the position is either on stage or
 * backstage, depending on their positionNumber
 *
 * The first maxStageParticipant slots are reserved for stage
 * The remainind maxBackstageParticipant slots are reserverd for backstage
 * Anything else is an error
 *
 * @param roomType RoomType, "Auditorium", "Reception", etc
 * @param positionNumber the 0 indexed position in the room
 * @returns "RoomLocation" or "StageLocation" or "BackstageLocation"
 */
export const positionToRoomSubkind = (
  roomType: RoomType,
  positionNumber: number
):
  | "RoomLocation"
  | "StageLocation"
  | "BackstageLocation"
  | "FloorMicLocation"
  | "InvisibleObserverLocation" => {
  if (roomType !== "Auditorium") {
    return "RoomLocation";
  } else {
    if (positionNumber < maxStageParticipants) {
      return "StageLocation";
    } else if (positionNumber < maxStageParticipants + maxBackstageParticipants) {
      return "BackstageLocation";
    } else if (firstFloorMicIndex <= positionNumber && positionNumber <= lastFloorMicIndex) {
      return "FloorMicLocation";
    } else if (
      firstInvisibleObserverIndex <= positionNumber &&
      positionNumber <= lastInvisibleObserverIndex
    ) {
      return "InvisibleObserverLocation";
    } else {
      logger.error(
        `Invalid room subtype encountered roomType ${roomType} positionNumber ${positionNumber}`
      );
      return "RoomLocation";
    }
  }
};

export const isFocusRoomType = (roomType: RoomType | undefined): boolean => {
  return isAllowedRoomType(roomType, ["MeetingRoom", "Auditorium", "CommandCenter"]);
};

export const isVideoRoomType = (roomType: RoomType | undefined): boolean => {
  return isAllowedRoomType(roomType, ["MeetingRoom", "Auditorium"]);
};

export const roomTypeHasMeetingChat = (roomType: RoomType | undefined): boolean => {
  return isAllowedRoomType(roomType, ["MeetingRoom", "Auditorium", "CommandCenter"]);
};

const isAllowedRoomType = (
  roomType: RoomType | undefined,
  allowedRoomTypes: RoomType[]
): boolean => {
  if (!roomType) {
    return false;
  }
  return allowedRoomTypes.includes(roomType);
};

// See roam/shared/FloorGridLayout.ts for more information about layout
export const numRoomSeatPositions = (room: Room): number => {
  return Math.max(0, room.width * room.height);
};

export const defaultRoomAccessModes: { [roomType in RoomType]: AccessMode } = {
  Office: "KnockRequired",
  TeamRoom: "Open",
  MeetingRoom: "Open",
  Auditorium: "Open",
  CommandCenter: "Open",
};
